import React, { useEffect } from "react";
import { Outlet, useLocation } from '@tanstack/react-router';
import './assets/css/nucleo-icons.css';
import './assets/css/nucleo-svg.css';
import { UploadShim } from './UploadShim';

import './assets/css/web3-style.css';
import './assets/css/pixel-modal.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from "react-toastify";

import { Buffer } from 'buffer';
import './mobile.css';
import Footer from './components/Footer';
import Navbar from './layouts/Head/Navbar';
import { QueryClientProvider } from './providers/QueryClientProvider';
import { WagmiProviderWrapper } from './providers/WagmiProvider';
import { AuthProvider } from './lib/auth';
import { initSentry } from './lib/sentry';
import { ErrorBoundaryWithLocation } from './components/ErrorBoundaryWithLocation';
import { SimulationErrorModal } from './components/SimulationErrorModal';
import { setupGlobalErrorHandlers } from './lib/error-handler';
import { mintingDappRegistry } from './minting/registry';
import { loadComponentFromCode } from './minting/componentLoader';
import { mintingDappAPI } from './minting/services/mintingDappAPI';
import { I18nProvider } from './i18n/context';
import { setLocale, locales, getLocale } from './i18n/translator';

// Extend Window interface
interface WindowWithBuffer extends Window {
    Buffer: typeof Buffer;
    wsavail: boolean;
    appKit: any;
    targetProxy: any;
    targetProxy_nft: any;
    wsh?: WebSocket;
    evmChains: { chains: any[] };
    idCache: Map<string, any>;
    nftFetcher: any;
    uploadItems: any;
}
declare const window: WindowWithBuffer;

if (!window.Buffer) {
    window.Buffer = Buffer;
}

// Global state
let wsavail = false;

interface AppContextState {
    pi?: any;
    sPI?: any;
}

export const AppContext = React.createContext<{ state: AppContextState; appKit?: any }>({ state: {}, appKit: undefined });

const SUPPORTED_LOCALES = Object.keys(locales) as Array<keyof typeof locales>;
const LOCALE_REGEX = new RegExp(`^(${SUPPORTED_LOCALES.join('|')})$`);

const AppContent = ({ appKit }: { appKit: any }) => {
    const [pi, sPI] = React.useState<any>();
    const location = useLocation();

    useEffect(() => {
        if (window && window.scrollTo) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
    }, [location.pathname]);

    useEffect(() => {
        // Remove the static loader shell as soon as React mounts
        const loader = document.getElementById('loader-shell');
        if (loader && loader.parentNode) {
            loader.parentNode.removeChild(loader);
        }
    }, []);

    useEffect(() => {
        // Keep the active locale in sync with the URL prefix. Actual
        // redirecting of locale-less URLs is handled by the router's
        // root beforeLoad, so we only mirror the segment here.
        const pathname = location.pathname;
        const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
        if (first && LOCALE_REGEX.test(first)) {
            setLocale(first as typeof SUPPORTED_LOCALES[number]);
        }
    }, [location.pathname]);

    const webSocket = () => {
        // window.genws = new WebSocket("wss://stealthisnft.xyz/wsServer");
    };

    useEffect(() => {
        // Initialize Sentry and global error handlers (once on app mount)
        initSentry().catch(() => {})
        setupGlobalErrorHandlers()

        window.document.body.classList.add('g-sidenav-show');
        window.document.body.style.backgroundColor = '#f8f9fa !important';

        webSocket();
        UploadShim();
        import('./utils/AuctionManagerEventlistener.jsx').then(({ AuctionManagerEventListener }) => {
          AuctionManagerEventListener().catch((err: Error) => {
            console.error('AuctionManagerEventListener failed:', err);
          });
        }).catch((err: Error) => {
          console.error('Failed to load AuctionManagerEventListener:', err);
        });
        import('bootstrap/dist/js/bootstrap.bundle.min').catch(() => {});
    }, []);

    // Keep the old onload hook for anything that genuinely must wait for every
    // asset to finish; dropdown wiring is intentionally above so it doesn't
    // depend on window.load.
    useEffect(() => {
        const handler = () => {};
        window.addEventListener('load', handler);
        return () => window.removeEventListener('load', handler);
    }, []);

    // Initialize minting dapp registry from backend API
    useEffect(() => {
      const initMintingRegistry = async () => {
        try {
          const result = await mintingDappAPI.getAll({ limit: 100 });
          if (result.success) {
            const apiDapps = result.data;
            const apiIds = new Set(apiDapps.map((d: any) => d.id));

            // Remove dapps from registry that are no longer in the API
            const currentIds = mintingDappRegistry.getIds();
            currentIds.forEach(regId => {
              if (!apiIds.has(regId)) {
                mintingDappRegistry.unregister(regId);
              }
            });

            // Add/update dapps from API, preserving internal components
            apiDapps.forEach((dapp: any) => {
              const existing = mintingDappRegistry.get(dapp.id);
              let finalDapp = dapp;
              if (dapp.type === 'internal') {
                if (existing?.component) {
                  finalDapp = { ...dapp, component: existing.component };
                } else if (dapp.component_code) {
                  try {
                    const component = loadComponentFromCode(dapp.component_code, dapp.id);
                    finalDapp = { ...dapp, component };
                  } catch (err) {
                    console.error(`[Minting] Failed to load component for ${dapp.id}:`, err);
                  }
                }
              }
              mintingDappRegistry.register(finalDapp);
            });
            console.log(`[Minting] Registry initialized with ${apiDapps.length} dapps from API`);
          }
        } catch (error) {
          console.error('[Minting] Failed to initialize registry:', error);
        }
      };

      initMintingRegistry();
    }, []);

    return (
        <QueryClientProvider>
            <WagmiProviderWrapper>
            <AuthProvider>
                <I18nProvider>
                <AppContext.Provider value={{ state: { pi, sPI }, appKit }}>
                    <div className="App d-flex flex-column min-vh-100">
                        <Navbar />
                        <div id="notify" style={{ zIndex: "4000000" }}>
                            <ToastContainer
                                className="toast-position"
                                position="bottom-center"
                                autoClose={3000}
                                hideProgressBar={false}
                                newestOnTop={false}
                                closeOnClick
                                rtl={false}
                                pauseOnFocusLoss
                                draggable
                                pauseOnHover
                                theme="dark"
                            />
                        </div>

                        <ErrorBoundaryWithLocation>
                            <Outlet />
                        </ErrorBoundaryWithLocation>
                        <Footer />
                        <SimulationErrorModal />
                    </div>
                </AppContext.Provider>
                </I18nProvider>
            </AuthProvider>
            </WagmiProviderWrapper>
        </QueryClientProvider>
    );
};

const App = () => {
    const [appKit, setAppKit] = React.useState<any>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [isClient, setIsClient] = React.useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!isClient) return;

        const initAppKit = async () => {
            try {
                const appKitReact = await import('@reown/appkit/react');
                const adapterModule = await import("@reown/appkit-adapter-ethers5");
                const Ethers5Adapter = adapterModule.default || adapterModule.Ethers5Adapter;
                if (!Ethers5Adapter) {
                    throw new Error("Ethers5Adapter not found. Keys: " + Object.keys(adapterModule).join(', '));
                }

                const networksModule = await import("@reown/appkit/networks");
                const mainnet = networksModule.mainnet || networksModule.default?.mainnet;
                const arbitrum = networksModule.arbitrum || networksModule.default?.arbitrum;
                if (!mainnet || !arbitrum) {
                    throw new Error("Failed to load networks. Keys: " + Object.keys(networksModule).join(', '));
                }

                const customChains = await import("./lib/reowncustomchains");
                const { coston, coston2, flare, songbird } = customChains;

                const projectId = "518587da28b6a74d92f9b44b6523334e";
                const metadata = {
                    name: "Test",
                    description: "Test",
                    url: window.location.origin,
                    icons: ["https://walletconnect.com/walletconnect-logo.png"]
                };
                const methods = ["eth_sendTransaction", "personal_sign", "eth_signTypedData", "wallet_switchEthereumChain"];
                const events = ["accountsChanged", "chainChanged"];

                const { CoreHelperUtil } = await import('@reown/appkit-controllers');
                const { PACKAGE_VERSION } = await import('@reown/appkit');
                const sdkVersion = CoreHelperUtil.generateSdkVersion([new Ethers5Adapter()], 'react', PACKAGE_VERSION);

                const instance = new appKitReact.AppKit({
                    enableInjected: true,
                    enableWalletConnect: true,
                    universalProviderConfigOverride: {
                        methods: { eip155: methods },
                        events: { eip155: events },
                    },
                    defaultNetwork: mainnet,
                    enableNetworkSwitch: true,
                    enableReconnect: true,
                    debug: true,
                    allowUnsupportedChain: true,
                    adapters: [new Ethers5Adapter()],
                    metadata: metadata,
                    networks: [mainnet, arbitrum, flare, songbird],
                    projectId: projectId,
                    sdkVersion,
                });

                instance.subscribeEvents((event) => {
                });

                appKitReact.getAppKit(instance);
                window.appKit = instance;
                setAppKit(instance);
            } catch (error: any) {
                console.error("[AppKit] Initialization failed:", error);
                console.error("[AppKit] Stack:", error.stack);
                setError(error.message || "Unknown error");
                setAppKit(null);
            }
        };

        initAppKit();
    }, [isClient]);

    if (error) {
        return (
            <div style={{ color: 'red', padding: '20px', backgroundColor: '#fff', minHeight: '100vh' }}>
                <h2>Wallet Connection Error</h2>
                <p>{error}</p>
                <p>Please check the browser console (F12) for more details.</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
            </div>
        );
    }

    return <AppContent appKit={appKit} />;
};

export default App;
